<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const formData = ref({
  name: '',
  domain: '',
  description: '',
  website: '',
  industry: '',
  size: '',
  revenue: '',
  phone: '',
  address: '',
});

const rules = {
  name: { required },
};

const v$ = useVuelidate(rules, formData);

const isFormInvalid = computed(() => v$.value.$invalid);

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const resetForm = () => {
  formData.value = {
    name: '',
    domain: '',
    description: '',
    website: '',
    industry: '',
    size: '',
    revenue: '',
    phone: '',
    address: '',
  };
  if (v$.value) {
    v$.value.$reset();
  }
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;
  try {
    // Filter out empty values
    const data = Object.fromEntries(
      Object.entries(formData.value).filter(([_, v]) => v.trim() !== '')
    );
    
    emit('create', data);
    close();
  } catch (error) {
    useAlert(t('COMPANIES.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const onSuccess = () => {
  resetForm();
  close();
};

defineExpose({
  dialogRef,
  open,
  close,
  onSuccess,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('COMPANIES.CREATE_DIALOG.TITLE')"
    :description="t('COMPANIES.CREATE_DIALOG.DESCRIPTION')"
  >
    <div class="flex flex-col gap-4 py-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="formData.name"
          :label="t('COMPANIES.FORM.NAME_LABEL')"
          :placeholder="t('COMPANIES.FORM.NAME_PLACEHOLDER')"
          :error="v$.name.$error ? t('COMPANIES.FORM.NAME_REQUIRED') : ''"
          autofocus
        />
        <Input
          v-model="formData.domain"
          :label="t('COMPANIES.FORM.DOMAIN_LABEL')"
          :placeholder="t('COMPANIES.FORM.DOMAIN_PLACEHOLDER')"
        />
      </div>
      
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('COMPANIES.FORM.DESCRIPTION_LABEL') }}
        </label>
        <Editor
          :model-value="formData.description || ''"
          :placeholder="t('COMPANIES.FORM.DESCRIPTION_PLACEHOLDER')"
          class="[&>div]:min-h-[120px]"
          @update:model-value="val => formData.description = val"
        />
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="formData.website"
          :label="t('COMPANIES.FORM.WEBSITE_LABEL')"
          :placeholder="t('COMPANIES.FORM.WEBSITE_PLACEHOLDER')"
        />
        <Input
          v-model="formData.industry"
          :label="t('COMPANIES.FORM.INDUSTRY_LABEL')"
          :placeholder="t('COMPANIES.FORM.INDUSTRY_PLACEHOLDER')"
        />
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="formData.size"
          :label="t('COMPANIES.FORM.SIZE_LABEL')"
          :placeholder="t('COMPANIES.FORM.SIZE_PLACEHOLDER')"
        />
        <Input
          v-model="formData.revenue"
          :label="t('COMPANIES.FORM.REVENUE_LABEL')"
          :placeholder="t('COMPANIES.FORM.REVENUE_PLACEHOLDER')"
        />
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="formData.phone"
          :label="t('COMPANIES.FORM.PHONE_LABEL')"
          :placeholder="t('COMPANIES.FORM.PHONE_PLACEHOLDER')"
        />
        <Input
          v-model="formData.address"
          :label="t('COMPANIES.FORM.ADDRESS_LABEL')"
          :placeholder="t('COMPANIES.FORM.ADDRESS_PLACEHOLDER')"
        />
      </div>
    </div>
    
    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          :label="t('COMPANIES.CANCEL')"
          variant="ghost"
          @click="close"
        />
        <Button
          :label="t('COMPANIES.CREATE_DIALOG.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isFormInvalid || isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
